import React from "react";
import styled from "styled-components";
import { Card, Tag, Button, Space, Typography } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  CarOutlined,
  PhoneOutlined,
  MessageOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

const JobCard = styled(Card)`
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const RouteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const RouteText = styled.div`
  flex: 1;
`;

const AddressLine = styled.div`
  font-size: 13px;
  color: #666;
  margin: 4px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  font-size: 12px;
  color: #666;
`;

const ActionButtons = styled(Space)`
  margin-top: 12px;
  width: 100%;
  justify-content: flex-end;
`;

const getStatusConfig = (status) => {
  switch (status) {
    case "in_progress":
      return {
        color: "success",
        icon: <PlayCircleOutlined />,
        label: "In Progress",
      };
    case "confirmed":
      return {
        color: "processing",
        icon: <CheckCircleOutlined />,
        label: "Confirmed",
      };
    case "scheduled":
      return {
        color: "warning",
        icon: <ClockCircleOutlined />,
        label: "Scheduled",
      };
    default:
      return {
        color: "default",
        icon: null,
        label: status?.replace("_", " ") || "Unknown",
      };
  }
};

const formatDate = (date) => {
  if (!date) return "Not scheduled";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ActiveJobCard = ({ job, onView }) => {
  const navigate = useNavigate();
  const statusConfig = getStatusConfig(job.status);

  const handleCardClick = () => {
    if (onView) {
      onView(job);
    } else {
      navigate(`/orders/${job.orderId}`);
    }
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    if (!job.customerPhone) return;
    const whatsappUrl = `https://wa.me/${job.customerPhone.replace(/[^0-9]/g, "")}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (!job.customerPhone) return;
    window.location.href = `tel:${job.customerPhone}`;
  };

  return (
    <JobCard onClick={handleCardClick}>
      <RouteHeader>
        <RouteText>
          <Text strong style={{ fontSize: 14 }}>
            {job.customerName}
          </Text>
          <div style={{ marginTop: 8 }}>
            <AddressLine>
              <Text type="secondary">📍</Text>
              <Text ellipsis style={{ maxWidth: "200px" }}>
                {job.pickupAddress}
              </Text>
            </AddressLine>
            <AddressLine>
              <Text type="secondary">→</Text>
              <Text ellipsis style={{ maxWidth: "200px" }}>
                {job.dropoffAddress}
              </Text>
            </AddressLine>
          </div>
        </RouteText>
        <Tag color={statusConfig.color} icon={statusConfig.icon}>
          {statusConfig.label}
        </Tag>
      </RouteHeader>

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
        <InfoRow>
          <CalendarOutlined />
          <Text type="secondary">Pickup Date:</Text>
          <Text strong>{formatDate(job.moveDate)}</Text>
        </InfoRow>

        {job.pickupTimeWindow && job.pickupTimeWindow !== "Not specified" && (
          <InfoRow>
            <ClockCircleOutlined />
            <Text type="secondary">Pickup Time:</Text>
            <Text>{job.pickupTimeWindow}</Text>
          </InfoRow>
        )}

        <InfoRow>
          <TeamOutlined />
          <Text type="secondary">Team:</Text>
          <Text>{job.teamName}</Text>
        </InfoRow>

        {job.primaryDriver && (
          <InfoRow>
            <UserOutlined />
            <Text type="secondary">Driver:</Text>
            <Text>{job.primaryDriver}</Text>
          </InfoRow>
        )}

        {job.vehicle && (
          <InfoRow>
            <CarOutlined />
            <Text type="secondary">Vehicle:</Text>
            <Text>{job.vehicle}</Text>
          </InfoRow>
        )}

        <InfoRow>
          <Text type="secondary">Order:</Text>
          <Text code style={{ fontSize: "11px" }}>
            {job.orderId}
          </Text>
        </InfoRow>
      </div>

      <ActionButtons>
        {job.customerPhone && (
          <>
            <Button
              size="small"
              icon={<PhoneOutlined />}
              onClick={handleCall}
            >
              Call
            </Button>
            <Button
              size="small"
              icon={<MessageOutlined />}
              onClick={handleWhatsApp}
            >
              WhatsApp
            </Button>
          </>
        )}
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          View
        </Button>
      </ActionButtons>
    </JobCard>
  );
};

export default ActiveJobCard;

